import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import TestingReportsAPI from '../Api/testing-reports';
import type { TestingReport } from '../Types/testing-report';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_FILE_TYPE = 'application/pdf';

export function validateFile(file: File): string | null {
  if (!file) {
    return 'No file selected';
  }

  const isNotPdf =
    file.type !== ACCEPTED_FILE_TYPE &&
    !file.name.toLowerCase().endsWith('.pdf');

  if (isNotPdf) {
    return 'Only PDF files are allowed';
  }

  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return `File size must not exceed 5MB. Your file is ${sizeMB}MB`;
  }

  return null;
}

const testingReportsQueryKey = (userId: string, poNumber: string) => [
  'testing-reports',
  userId,
  poNumber,
];

type UseGetTestingReportsOptions = {
  enabled?: boolean;
};

export function useGetTestingReports(
  userId: string,
  poNumber: string,
  options: UseGetTestingReportsOptions = {},
) {
  const { enabled = true } = options;

  return useQuery<TestingReport[]>(
    testingReportsQueryKey(userId, poNumber),
    () => TestingReportsAPI.getTestingReports(userId, poNumber),
    {
      enabled: enabled && !!userId && !!poNumber,
      staleTime: 1000 * 60 * 5,
    },
  );
}

type UploadParams = {
  userId: string;
  poNumber: string;
  file: File;
};

type UploadResult = {
  reportId: string;
};

export function useUploadTestingReport() {
  const queryClient = useQueryClient();
  const [uploadProgress, setUploadProgress] = useState(0);

  const mutation = useMutation<UploadResult, Error, UploadParams>(
    async ({ userId, poNumber, file }) => {
      setUploadProgress(0);

      const report = await TestingReportsAPI.createTestingReport({
        userId,
        poNumber,
        fileName: file.name,
        fileSize: file.size,
      });

      await TestingReportsAPI.uploadFileToS3({
        uploadUrl: report.upload_url,
        file,
        onProgress: setUploadProgress,
      });

      await TestingReportsAPI.confirmTestingReport({
        userId,
        poNumber,
        reportId: report.id,
      });

      return { reportId: report.id };
    },
    {
      onSuccess: (_data, { userId, poNumber }) => {
        toast('Testing report uploaded successfully', { type: 'success' });
        queryClient.invalidateQueries(testingReportsQueryKey(userId, poNumber));
      },
      onError: (error: Error) => {
        toast(error.message || 'Failed to upload testing report', {
          type: 'error',
        });
      },
      onSettled: () => {
        setUploadProgress(0);
      },
    },
  );

  return {
    uploadTestingReport: mutation.mutate,
    isUploading: mutation.isLoading,
    uploadProgress,
  };
}
